import { PackagingBottomSheet } from '@components/BottomSheet/PackagingBottomSheet';
import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderDetailsCard } from '@components/OrderProcessing/OrderDetailsCard';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { useToast } from '@contexts/Toast';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import {
  faCircleCheck,
  faChevronDown,
  faChevronUp,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  errorMessage,
  Length,
  OrderCreationMasterData,
  OrderProcessingMasterData,
  OrderProcessStatus,
  ProductProcessingCrumbSteps,
  ProductType,
  responseTexts,
  timers,
  WeightType,
} from '@helpers/Constants';
import { FrontendRoutes, ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  MasterPackage,
  OrderDetailsCardInfo,
  OrderDetailsResponse,
  PackagingOption,
  ProductProcessingOrderSpecies,
  ProductProcessingPacking,
  ProductProcessingProduct,
} from '@helpers/Models';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface ProductDraft {
  quantity: number;
  note: string;
  noteId?: number;
}

export const ProductProcessingTaskList: React.FC = () => {
  const { state } = useLocation();
  const orderId = state?.orderId;
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();
  const navigate = useNavigate();

  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse>();
  const [productDetailsBySpecies, setProductDetailsBySpecies] = useState<
    Record<number, ProductProcessingOrderSpecies>
  >({});
  const [productDrafts, setProductDrafts] = useState<Record<number, ProductDraft>>(
    {},
  );
  const [directCompleteProductId, setDirectCompleteProductId] = useState<
    number | null
  >(null);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [loadingSpeciesIds, setLoadingSpeciesIds] = useState<number[]>([]);
  const [masterPackages, setMasterPackages] = useState<MasterPackage[]>([]);
  const [finalWeightByProduct, setFinalWeightByProduct] = useState<
    Record<number, number | string>
  >({});
  const [finalPackageByProduct, setFinalPackageByProduct] = useState<
    Record<number, ProductProcessingPacking[]>
  >({});
  const [packageIndexByProduct, setPackageIndexByProduct] = useState<
    Record<number, number>
  >({});
  const [activeProductForPackaging, setActiveProductForPackaging] = useState<
    number | null
  >(null);
  const [showPackagingBottomSheet, setShowPackagingBottomSheet] =
    useState(false);
  const [activeNotesSpeciesId, setActiveNotesSpeciesId] = useState<number | null>(
    null,
  );
  const [percentageError, setPercentageError] = useState<boolean>(false);

  const showAddMorePackagingButton = (() => {
    try {
      const modules = JSON.parse(localStorage.getItem('modules') ?? '[]');
      return Array.isArray(modules) && modules.includes(5);
    } catch {
      return false;
    }
  })();

  const addonTagColorClasses = [
    'bg-[#EAF4FF] border-[#BFD9FF] text-[#1D4E89]',
    'bg-[#EAFBF3] border-[#B9E9D0] text-[#166C45]',
    'bg-[#FFF6E8] border-[#FFDDB3] text-[#8A4B12]',
    'bg-[#F5EEFF] border-[#DCC8FF] text-[#5A2CA0]',
    'bg-[#FFEFF1] border-[#FFC9D0] text-[#9A1F35]',
  ];

  useEffect(() => {
    fetchMasterPackages();

    if (orderId) {
      fetchOrderDetails();
    } else {
      showToastMessage(errorMessage.INVALID_REQUEST, 'error');
      setTimeout(() => {
        navigate(FrontendRoutes.PRODUCT_PROCESSING_LIST);
      }, timers.TOAST_AUTO_CLOSE);
    }
  }, [orderId]);

  const fetchMasterPackages = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      setMasterPackages(response.data.data.masterData.packageType);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const fetchOrderDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.ORDER_DETAILS, {
        id: orderId,
      });
      const data: OrderDetailsResponse = response.data.data;
      setOrderDetails(data);

      if (data?.species?.length > 0) {
        const speciesWithProducts = data.species.filter(
          (species) => species.products && species.products.length > 0,
        );
        setOpenAccordions(speciesWithProducts.length > 0 ? [0] : []);

        await Promise.allSettled(
          speciesWithProducts.map((species) =>
            fetchProductTaskDetailsBySpecies(species.speciesId, false),
          ),
        );
      }
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const fetchProductTaskDetailsBySpecies = async (
    orderSpeciesId: number,
    showErrorToast = true,
  ): Promise<void> => {
    setLoadingSpeciesIds((prev) =>
      prev.includes(orderSpeciesId) ? prev : [...prev, orderSpeciesId],
    );

    try {
      const response = await Axios.post(
        ApiEndpoints.PRODUCT_PROCESSING_TASK_DETAILS,
        {
          orderSpeciesId,
        },
      );

      const details = response.data.data?.[0] as ProductProcessingOrderSpecies;
      if (details) {
        setProductDetailsBySpecies((prev) => ({
          ...prev,
          [orderSpeciesId]: details,
        }));

        details.products.forEach((product) => {
          const nextPackaging =
            product.productStatus < OrderProcessStatus.COMPLETED
              ? product.packing.map((pack) => ({
                ...pack,
                isFinalPacking: 1,
                packingPercentage: pack.packingPercentage ?? 0,
              }))
              : product.packing.filter(
                (pack) => pack.isFinalPacking === 1,
              );

          setFinalPackageByProduct((prev) => ({
            ...prev,
            [product.orderProductId]: nextPackaging,
          }));
        });
      }
    } catch (error) {
      if (showErrorToast) {
        showToastMessage(errorMessage.FETCH_ERROR, 'error');
      }
    } finally {
      setLoadingSpeciesIds((prev) =>
        prev.filter((id) => id !== orderSpeciesId),
      );
    }
  };

  const toggleAccordion = (index: number): void => {
    const isOpening = !openAccordions.includes(index);
    setOpenAccordions((prev) => (prev.includes(index) ? [] : [index]));

    if (!isOpening) {
      return;
    }

    const species = speciesWithProducts[index];
    if (!species) {
      return;
    }

    if (!productDetailsBySpecies[species.speciesId]) {
      fetchProductTaskDetailsBySpecies(species.speciesId);
    }
  };

  const buildDefaultDraft = (product: ProductProcessingProduct): ProductDraft => ({
    quantity:
      product.productFinalQuantity && product.productFinalQuantity !== 0
        ? product.productFinalQuantity
        : product.productQuantity,
    note: product.orderProductNotes?.[0]?.note ?? '',
    noteId: product.orderProductNotes?.[0]?.id,
  });

  const ensureDraft = (product: ProductProcessingProduct): void => {
    setProductDrafts((prev) => {
      if (prev[product.orderProductId]) {
        return prev;
      }

      return {
        ...prev,
        [product.orderProductId]: buildDefaultDraft(product),
      };
    });
  };

  const getDraftWithFallback = (
    prevDraft: ProductDraft | undefined,
    product: ProductProcessingProduct,
  ): ProductDraft => prevDraft ?? buildDefaultDraft(product);

  const updateDraft = (
    productId: number,
    values: Partial<ProductDraft>,
    product?: ProductProcessingProduct,
  ): void => {
    setProductDrafts((prev) => {
      const existing = product
        ? getDraftWithFallback(prev[productId], product)
        : prev[productId] ?? { quantity: 0, note: '' };

      return {
        ...prev,
        [productId]: {
          ...existing,
          ...values,
        },
      };
    });
  };

  const handleStartProcessing = async (
    speciesId: number,
    product: ProductProcessingProduct,
  ): Promise<void> => {
    try {
      await Axios.post(ApiEndpoints.PRODUCT_PROCESS_WORKFLOW, {
        productId: product.orderProductId,
        status: OrderProcessStatus.INPROGRESS,
      });

      showToastMessage(responseTexts.PRODUCT_WORKFLOW_START_SUCCESS, 'success');
      await fetchProductTaskDetailsBySpecies(speciesId);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_PRODUCT_WORKFLOW, 'error');
    }
  };

  const upsertProductNote = async (
    speciesDetails: ProductProcessingOrderSpecies,
    product: ProductProcessingProduct,
    draft: ProductDraft,
  ): Promise<void> => {
    const note = draft.note.trim();
    if (!note) {
      return;
    }

    const response = await Axios.post(ApiEndpoints.PRODUCT_PROCESS_UPSERT_NOTES, {
      orderId: speciesDetails.orderId,
      orderSpeciesId: speciesDetails.orderSpeciesId,
      orderProductId: product.orderProductId,
      note,
      ...(draft.noteId ? { id: draft.noteId } : {}),
    });

    const updatedNote = response?.data?.data;
    if (updatedNote?.id) {
      updateDraft(product.orderProductId, { noteId: updatedNote.id });
    }
  };

  const handleResetProduct = async (product: ProductProcessingProduct): Promise<void> => {
    const confirmReset = async (): Promise<void> => {
      try {
        await Axios.post(ApiEndpoints.PRODUCT_PROCESS_RESTORE, {
          orderProductId: product.orderProductId,
        });

        showToastMessage('Product reset successfully.', 'success');
        await fetchOrderDetails();
      } catch (error) {
        showToastMessage('Failed to reset product.', 'error');
      }
    };

    showConfirmationToast(
      'The service data will go to its original state losing all the current changes. Do you wish to continue',
      'Reset Product',
      'delete',
      confirmReset,
      'Reset',
      undefined,
      'Cancel',
    );
  };

  const handleSelectPackaging = (
    productId: number,
    packId: PackagingOption,
  ): void => {
    if (packageIndexByProduct[productId] === undefined) return;

    const selectedPackage = masterPackages.find(
      (item) => item.id === packId.productPackageTypeId,
    );
    if (!selectedPackage) return;

    const speciesEntry = Object.values(productDetailsBySpecies).find((species) =>
      species.products.some((product) => product.orderProductId === productId),
    );
    const currentFinalPackages =
      finalPackageByProduct[productId] ??
      speciesEntry?.products.find((product) => product.orderProductId === productId)
        ?.packing ??
      [];

    const updatedPacking = [...currentFinalPackages];
    const packageIndex = packageIndexByProduct[productId] ?? updatedPacking.length;
    const currentPackIdInSlot = updatedPacking[packageIndex]?.orderPackingId;

    const alreadyExists = updatedPacking.some(
      (pack) =>
        pack.productPackageTypeLuId === selectedPackage.id &&
        pack.isFinalPacking === 1 &&
        pack.orderPackingId !== currentPackIdInSlot,
    );

    if (alreadyExists) {
      showToastMessage(errorMessage.ORDER_DUPLICATE_PACKAGE, 'error');
      return;
    }

    const newPackageSelected: ProductProcessingPacking = {
      orderPackingId: selectedPackage.id,
      packageType: selectedPackage.packageType,
      packageSize: selectedPackage.packageSize,
      productPackageTypeLuId: selectedPackage.id,
      packingPrice: selectedPackage.price,
      packingPercentage: 0,
      isFinalPacking: 1,
    };

    if (packageIndex >= updatedPacking.length) {
      updatedPacking.push(newPackageSelected);
    } else {
      updatedPacking[packageIndex] = newPackageSelected;
    }

    const finalizedPackages = updatedPacking.filter(
      (pack) => pack.isFinalPacking === 1,
    );

    const finalUpdatedPacking =
      finalizedPackages.length === 2
        ? updatedPacking.map((pack) => {
          if (pack.isFinalPacking === 1) {
            return { ...pack, packingPercentage: 50 };
          }
          return pack;
        })
        : finalizedPackages.length === 1
          ? updatedPacking.map((pack) => {
            if (pack.isFinalPacking === 1) {
              return { ...pack, packingPercentage: 100 };
            }
            return pack;
          })
          : updatedPacking;

    setFinalPackageByProduct((prev) => ({
      ...prev,
      [productId]: finalUpdatedPacking,
    }));
    setShowPackagingBottomSheet(false);
    setActiveProductForPackaging(null);
  };

  const handleDeletePacking = (
    productId: number,
    orderPackingId: number,
  ): void => {
    const finalPackages =
      finalPackageByProduct[productId]?.filter((pack) => pack.isFinalPacking === 1) ??
      [];
    const remainingPacking = finalPackages.filter(
      (pack) => pack.orderPackingId !== orderPackingId,
    );

    if (remainingPacking.length === 0) {
      showToastMessage(errorMessage.ORDER_PACKAGE_COUNT_ERROR, 'error');
      return;
    }

    setFinalPackageByProduct((prev) => ({
      ...prev,
      [productId]: finalPackages.filter(
        (pack) => pack.orderPackingId !== orderPackingId,
      ),
    }));
  };

  const handlePercentageBlur = (value: number): void => {
    const intValue = Math.round(value);
    if (isNaN(intValue) || intValue <= 0 || intValue >= 100) {
      setPercentageError(true);
      showToastMessage(errorMessage.ORDER_PACKAGE_PERCENTAGE_INVALID, 'error');
    } else {
      setPercentageError(false);
    }
  };

  const handlePercentageChange = (
    productId: number,
    packageId: number,
    rawValue: number | string,
  ): void => {
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      setFinalPackageByProduct((prev) => ({
        ...prev,
        [productId]: (prev[productId] ?? []).map((pack) =>
          pack.orderPackingId === packageId
            ? { ...pack, packingPercentage: 0 }
            : pack,
        ),
      }));
      return;
    }

    const numValue = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
    if (isNaN(numValue)) return;

    const intValue = Math.max(0, Math.min(100, Math.floor(Math.abs(numValue))));

    setFinalPackageByProduct((prev) => {
      const updatedPacking = [...(prev[productId] ?? [])];
      const packagingIndex = updatedPacking.findIndex(
        (pack) => pack.orderPackingId === packageId,
      );
      if (packagingIndex === -1) return prev;

      updatedPacking[packagingIndex] = {
        ...updatedPacking[packagingIndex],
        packingPercentage: intValue,
        isFinalPacking: 1,
      };

      const finalPackages = updatedPacking.filter((pack) => pack.isFinalPacking === 1);
      if (finalPackages.length === 2) {
        const otherPackage = finalPackages.find((pack) => pack.orderPackingId !== packageId);
        if (otherPackage) {
          const otherIndex = updatedPacking.findIndex(
            (pack) => pack.orderPackingId === otherPackage.orderPackingId,
          );
          const otherValue = 100 - intValue;
          updatedPacking[otherIndex] = {
            ...updatedPacking[otherIndex],
            packingPercentage: Math.max(0, Math.min(100, otherValue)),
          };
        }
      }

      return {
        ...prev,
        [productId]: updatedPacking,
      };
    });
  };

  const handleCompleteProcessing = async (
    speciesId: number,
    speciesDetails: ProductProcessingOrderSpecies,
    product: ProductProcessingProduct,
  ): Promise<void> => {
    const draft =
      productDrafts[product.orderProductId] ?? buildDefaultDraft(product);

    const effectiveFinalQuantity =
      product.productType === ProductType.WEIGHT
        ? Number(
          finalWeightByProduct[product.orderProductId] ??
          draft.quantity ??
          product.productQuantity,
        )
        : Number(draft.quantity ?? product.productQuantity);

    if (
      product.productType === ProductType.WEIGHT &&
      (!effectiveFinalQuantity || effectiveFinalQuantity <= 0)
    ) {
      showToastMessage(errorMessage.PRODUCT_FINAL_WEIGHT_REQUIRED, 'error');
      return;
    }

    const finalPackaging = finalPackageByProduct[product.orderProductId] ?? product.packing;
    const totalPercentage = finalPackaging.reduce(
      (sum, pack) => sum + (pack.packingPercentage ?? 0),
      0,
    );

    if (finalPackaging.length > 1 && totalPercentage !== 100) {
      showToastMessage(errorMessage.ORDER_PACKAGE_PERCENTAGE_SPLIT_ERROR, 'error');
      return;
    }

    try {
      await Axios.post(ApiEndpoints.PRODUCT_PROCESS_WORKFLOW, {
        productId: product.orderProductId,
        status: OrderProcessStatus.COMPLETED,
        finalQuantity: effectiveFinalQuantity,
        packaging: finalPackaging.map((pack) => ({
          productPackageTypeId: pack.productPackageTypeLuId ?? pack.orderPackingId,
          percentage: pack.packingPercentage ?? 100,
        })),
      });

      await upsertProductNote(speciesDetails, product, draft);

      showToastMessage(responseTexts.PRODUCT_WORKFLOW_FINISH_SUCCESS, 'success');
      setDirectCompleteProductId(null);
      setEditingProductId(null);
      await fetchProductTaskDetailsBySpecies(speciesId);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_PRODUCT_WORKFLOW, 'error');
    }
  };

  const handleCancelInlineEdit = (product: ProductProcessingProduct): void => {
    updateDraft(product.orderProductId, buildDefaultDraft(product));
    setDirectCompleteProductId(null);
    setEditingProductId(null);
  };

  const handleIncrement = (product: ProductProcessingProduct): void => {
    const current =
      productDrafts[product.orderProductId]?.quantity ??
      (product.productFinalQuantity && product.productFinalQuantity !== 0
        ? product.productFinalQuantity
        : product.productQuantity);

    updateDraft(product.orderProductId, {
      quantity: Number((current + 0.5).toFixed(2)),
    });
    if (product.productType === ProductType.WEIGHT) {
      setFinalWeightByProduct((prev) => ({
        ...prev,
        [product.orderProductId]: Number((current + 0.5).toFixed(2)),
      }));
    }
  };

  const handleDecrement = (product: ProductProcessingProduct): void => {
    const current =
      productDrafts[product.orderProductId]?.quantity ??
      (product.productFinalQuantity && product.productFinalQuantity !== 0
        ? product.productFinalQuantity
        : product.productQuantity);

    const nextValue = Number(Math.max(0, current - 0.5).toFixed(2));
    updateDraft(product.orderProductId, { quantity: nextValue });
    if (product.productType === ProductType.WEIGHT) {
      setFinalWeightByProduct((prev) => ({
        ...prev,
        [product.orderProductId]: nextValue,
      }));
    }
  };

  const orderDetailsCardInfo = {
    orderNumber: orderDetails?.orderInfo.orderNumber,
    createdByUser: orderDetails?.orderInfo.createdByUser,
    roles: orderDetails?.orderInfo.roles,
    customerName: orderDetails?.orderInfo.customerName,
    orderDate: orderDetails?.orderInfo.orderDate,
    customerEmail: orderDetails?.orderInfo.customerEmail,
    customerPhone: orderDetails?.orderInfo.customerPhone,
  } as OrderDetailsCardInfo;

  const BreadCrumbsProps = {
    steps: ProductProcessingCrumbSteps,
    activeItem: 'Product Processing -',
    orderNumber: orderDetails?.orderInfo?.orderNumber,
    type: OrderProcessingMasterData.PRODUCT_PROCESSING,
  };

  const speciesWithProducts =
    orderDetails?.species?.filter(
      (species) => species.products && species.products.length > 0,
    ) ?? [];

  const activeNotesSpecies =
    activeNotesSpeciesId === null
      ? undefined
      : orderDetails?.species.find(
        (species) => species.speciesId === activeNotesSpeciesId,
      );

  const renderFinalPackagingEditor = (product: ProductProcessingProduct) => {
    const finalPackaging =
      product.productStatus === OrderProcessStatus.COMPLETED
        ? (finalPackageByProduct[product.orderProductId] ??
          product.packing.filter((pack) => pack.isFinalPacking === 1))
        : (finalPackageByProduct[product.orderProductId] ?? product.packing);

    return (
      <div className="bg-[#FFFFFF] p-2 rounded-md">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg text-gray-800 font-medium">
            Choose final Packaging
          </h3>
        </div>

        {finalPackaging.map((pack, index) => (
          <div
            key={`${product.orderProductId}-${pack.orderPackingId ?? index}`}
            className="flex items-center mb-2 justify-between bg-white"
          >
            <div
              className="flex-1 relative"
              onClick={() => {
                setActiveProductForPackaging(product.orderProductId);
                setPackageIndexByProduct((prev) => ({
                  ...prev,
                  [product.orderProductId]: index,
                }));
                setShowPackagingBottomSheet(true);
              }}
            >
              <div className="flex items-center border border-gray-300 rounded px-3 py-1 h-10 text-sm cursor-pointer">
                <div className="flex items-center w-full justify-between">
                  <span className="text-gray-800 truncate min-w-[80px] flex-shrink-0">
                    {pack.packageType}
                  </span>
                  <span className="text-gray-600 text-center min-w-[80px] flex-shrink-0">
                    {pack.packageSize} {WeightType.LBS}
                  </span>
                  <span className="font-semibold text-right min-w-[100px] flex-shrink-0">
                    + ${pack.packingPrice} /{WeightType.LBS}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-[#A09CAB] text-sm"
                  />
                </div>
              </div>
            </div>

            {(product.productStatus === OrderProcessStatus.COMPLETED
              ? finalPackaging.filter((packItem) => packItem.isFinalPacking === 1)
                .length > 1
              : finalPackaging.length > 1) && (
                <div className="relative w-[80px] ml-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={pack.packingPercentage}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const digits = raw.replace(/\D/g, '');
                      if (digits === '') {
                        handlePercentageChange(
                          product.orderProductId,
                          pack.orderPackingId,
                          0,
                        );
                        return;
                      }
                      const num = parseInt(digits, 10);
                      handlePercentageChange(
                        product.orderProductId,
                        pack.orderPackingId,
                        num,
                      );
                    }}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val)) {
                        handlePercentageBlur(val);
                      }
                    }}
                    className={`h-10 w-full pr-5 pl-2 border rounded-md focus:outline-primary text-center ${percentageError ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    %
                  </span>
                </div>
              )}

            <button
              type="button"
              onClick={() =>
                handleDeletePacking(product.orderProductId, pack.orderPackingId)
              }
              className="mr-2 ml-4 py-1.5 px-2 rounded border text-gray-800"
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
          </div>
        ))}

        {showAddMorePackagingButton &&
          finalPackaging.filter((pack) => pack.isFinalPacking === 1).length < 2 && (
            <button
              type="button"
              onClick={() => {
                setActiveProductForPackaging(product.orderProductId);
                setPackageIndexByProduct((prev) => ({
                  ...prev,
                  [product.orderProductId]: finalPackaging.length,
                }));
                setShowPackagingBottomSheet(true);
              }}
              className="w-full text-primary border bg-white border-primary font-medium py-1 rounded mb-4 mt-2"
            >
              Add More Packaging
            </button>
          )}
      </div>
    );
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      <SideNav active={5} />
      <div className="flex-1 p-1 overflow-auto relative">
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="p-1"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                Product Processing - {orderDetails?.orderInfo?.orderNumber}
              </h2>
            </div>
          </div>

          {orderDetails && (
            <OrderDetailsCard orderDetailsCardInfo={orderDetailsCardInfo} />
          )}

          <h1 className="font-semibold text-xl mt-3">Product Processing Tasks</h1>

          {speciesWithProducts.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              No species with products available for processing.
            </div>
          ) : (
            speciesWithProducts.map((speciesInfo, index) => (
              <div key={speciesInfo.speciesId}>
                <div className="border rounded-md shadow-sm mb-3 mt-4">
                  <button
                    type="button"
                    onClick={() => toggleAccordion(index)}
                    className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg focus:outline-primary"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-xl font-bold text-gray-900">
                        {speciesInfo.speciesTag}
                        {speciesInfo.checkIn && speciesInfo.checkIn.inches && (
                          <>
                            {' '}({speciesInfo.checkIn.feet} {Length.FEET}{' '}
                            {speciesInfo.checkIn.inches} {Length.INCHES})
                          </>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setActiveNotesSpeciesId(speciesInfo.speciesId);
                        }}
                        className="text-primary p-1 text-sm font-bold"
                      >
                        View Notes
                      </button>
                    </span>
                    <div className="flex items-center gap-4">
                      <FontAwesomeIcon
                        className="text-[#000A16]"
                        icon={
                          openAccordions.includes(index)
                            ? faChevronUp
                            : faChevronDown
                        }
                      />
                    </div>
                  </button>

                  {openAccordions.includes(index) && (
                    <div>
                      {loadingSpeciesIds.includes(speciesInfo.speciesId) ? (
                        <div className="p-3 text-gray-500">Loading products...</div>
                      ) : productDetailsBySpecies[speciesInfo.speciesId]?.products
                        ?.length ? (
                        <div>
                          {productDetailsBySpecies[speciesInfo.speciesId].products.map(
                            (product) => {
                              const productStatus = Number(product.productStatus ?? 0);
                              const isInitial =
                                productStatus === OrderProcessStatus.INITIAL;
                              const isInProgress =
                                productStatus === OrderProcessStatus.INPROGRESS;
                              const isCompleted =
                                productStatus >= OrderProcessStatus.COMPLETED;
                              const isFinalized =
                                productStatus > OrderProcessStatus.COMPLETED;
                              const isDirectComplete =
                                directCompleteProductId === product.orderProductId;
                              const isEditingCompleted =
                                editingProductId === product.orderProductId;
                              const displayQuantity =
                                product.quantity ?? product.productQuantity;
                              const productCardNote =
                                product.note?.trim() ||
                                product.orderPackingNotes?.trim() ||
                                product.orderProductNotes?.[0]?.note?.trim() ||
                                '';
                              const showInlineEditor =
                                isInProgress ||
                                isDirectComplete ||
                                isEditingCompleted;
                              const draft =
                                productDrafts[product.orderProductId] ??
                                buildDefaultDraft(product);

                              return (
                                <div
                                  key={product.orderProductId}
                                  className={`rounded-md border shadow-md mt-4 mx-3 ${showInlineEditor ? 'bg-quaternary' : 'bg-[#FFFFFF]'
                                    }`}
                                >
                                  <div className="p-3 rounded-md flex items-center gap-4">
                                    <div className="flex-1 space-y-2">
                                      <div className="flex justify-between items-center">
                                        <p className="text-lg font-semibold">
                                          {product.productName}
                                          {' - '}
                                          {displayQuantity}
                                          {product.productType === ProductType.WEIGHT
                                            ? ` ${WeightType.LBS}`
                                            : ` ${WeightType.QUANTITY}`}
                                          {product.fatPercent > 0 &&
                                            `, ${product.fatPercent}% Fat`}
                                        </p>
                                        <div className="flex items-center gap-2">
                                          {!isEditingCompleted && isCompleted && !isFinalized && (
                                            <>
                                              <button
                                                type="button"
                                                onClick={() => handleResetProduct(product)}
                                                className="font-bold text-red-600 flex items-center gap-2 hover:text-red-700"
                                              >
                                                Reset
                                              </button>
                                              <span className="text-[#00000033] text-2xl">
                                                |
                                              </span>
                                            </>
                                          )}
                                          {isCompleted && !isEditingCompleted && !isFinalized && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                ensureDraft(product);
                                                setEditingProductId(
                                                  product.orderProductId,
                                                );
                                              }}
                                              className="font-bold text-primary flex items-center gap-2"
                                            >
                                              <FontAwesomeIcon icon={faPen} />
                                              Edit
                                            </button>
                                          )}
                                          <OrderStatusBadge
                                            status={product.productStatus}
                                            type="productStatus"
                                          />
                                        </div>
                                      </div>

                                      {/* <div className="bg-[#F5F5F5] p-2 rounded-md text-sm inline-block w-full">
                                        {product.productType === ProductType.WEIGHT
                                          ? 'Weight-based product'
                                          : 'Quantity-based product'}
                                      </div> */}

                                      <div className="bg-[#F5F5F5] p-2 rounded-md text-sm w-full">
                                        <span className="font-semibold text-gray-800">
                                          Notes:
                                        </span>
                                        <p className="mt-1 text-[#43474E] break-words whitespace-pre-wrap leading-5">
                                          {productCardNote || errorMessage.NO_NOTES_FOUND}
                                        </p>
                                      </div>

                                      {product.addons?.length > 0 && (
                                        <div className="bg-[#F5F5F5] p-2 rounded-md text-sm w-full">
                                          <span className="font-semibold text-gray-800">Add-Ons:</span>
                                          <div className="flex flex-wrap gap-2 mt-2">
                                            {product.addons.map((addon, addonIndex) => (
                                              <span
                                                key={addon.addonId}
                                                className={`border rounded px-2 py-1 font-medium ${addonTagColorClasses[addonIndex % addonTagColorClasses.length]}`}
                                              >
                                                {addon.addonName}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {showInlineEditor && (
                                        <div className="bg-[#F1F1F1] rounded-md shadow-sm mt-5 p-3 border-[#00000033]">
                                          <span className="font-semibold">Order Details</span>
                                          <div className="flex items-center justify-between py-2 rounded-md">
                                            <div>
                                              <span className="text-[#43474E]">
                                                {product.productType === ProductType.WEIGHT
                                                  ? 'Total Weight: '
                                                  : 'Total Quantity: '}
                                              </span>
                                              <span className="text-gray-800 font-semibold">
                                                {product.productQuantity}{' '}
                                                {product.productType === ProductType.WEIGHT
                                                  ? WeightType.LBS
                                                  : ''}
                                              </span>
                                            </div>
                                            {product.productType === ProductType.QUANTITY && (
                                              <div>
                                                <span className="text-[#43474E] mt-2">
                                                  Total Estimated weight in Lbs:{' '}
                                                </span>
                                                <span className="text-gray-800 font-semibold">
                                                  {(product.productQuantity * product.weight).toFixed(2)}{' '}
                                                  {WeightType.LBS}
                                                </span>
                                              </div>
                                            )}
                                          </div>

                                          {product.packing?.length > 0 && (
                                            <div>
                                              <span className="font-semibold mb-2">Packaging:</span>
                                              <div className="space-y-2 py-2">
                                                {(product.productStatus >= OrderProcessStatus.COMPLETED
                                                  ? product.packing.filter(
                                                    (pack) => pack.isFinalPacking === 1,
                                                  )
                                                  : product.packing
                                                ).map((pack) => (
                                                  <div
                                                    key={pack.orderPackingId}
                                                    className="flex justify-between gap-3"
                                                  >
                                                    <span className="w-1/4 text-[#43474E]">
                                                      {pack.packageType}
                                                    </span>
                                                    <span className="w-1/4 text-center">
                                                      <span className="text-[#43474E]">Size: </span>
                                                      {pack.packageSize}{' '}
                                                      <span className="text-black">
                                                        {WeightType.LBS}
                                                      </span>
                                                    </span>
                                                    {product.packing.length > 1 && (
                                                      <span className="w-1/4 text-center">
                                                        <span className="text-[#43474E]">
                                                          Pkg Split:{' '}
                                                        </span>
                                                        <span className="text-black">
                                                          {pack.packingPercentage}%
                                                        </span>
                                                      </span>
                                                    )}
                                                    <span className="w-1/4 text-right">
                                                      <span className="text-[#43474E]">Price: </span>
                                                      <span className="text-black">
                                                        ${pack.packingPrice}
                                                      </span>
                                                    </span>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {showInlineEditor && (
                                        <div className="bg-[#FFFFFF] p-2 rounded-md flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                                          <span>
                                            {product.productType === ProductType.WEIGHT
                                              ? 'Final Weight after Processing'
                                              : 'Final quantity'}
                                          </span>
                                          <div className="flex items-center">
                                            <button
                                              type="button"
                                              onClick={() => handleDecrement(product)}
                                              className="w-12 h-8 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                                            >
                                              -
                                            </button>
                                            <input
                                              type="number"
                                              value={
                                                product.productType === ProductType.WEIGHT
                                                  ? (finalWeightByProduct[
                                                    product.orderProductId
                                                  ] ??
                                                    draft.quantity ??
                                                    product.productQuantity)
                                                  : draft.quantity
                                              }
                                              onChange={(e) => {
                                                if (product.productType === ProductType.WEIGHT) {
                                                  const nextValue = e.target.value;
                                                  setFinalWeightByProduct((prev) => ({
                                                    ...prev,
                                                    [product.orderProductId]: nextValue,
                                                  }));

                                                  if (nextValue !== '') {
                                                    updateDraft(
                                                      product.orderProductId,
                                                      {
                                                        quantity: Number(nextValue),
                                                      },
                                                      product,
                                                    );
                                                  }
                                                  return;
                                                }

                                                const nextValue =
                                                  Number(parseFloat(e.target.value) || 0);
                                                updateDraft(
                                                  product.orderProductId,
                                                  {
                                                    quantity: nextValue,
                                                  },
                                                  product,
                                                );
                                              }}
                                              className="w-20 h-8 text-center border-t border-b focus:outline-primary"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => handleIncrement(product)}
                                              className="w-12 h-8 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                                            >
                                              +
                                            </button>
                                            <span className="ml-2">
                                              {product.productType === ProductType.WEIGHT
                                                ? WeightType.LBS
                                                : 'Qty'}
                                            </span>
                                          </div>
                                        </div>
                                      )}

                                      {showInlineEditor && renderFinalPackagingEditor(product)}

                                      {showInlineEditor && (
                                        <div className="bg-[#FFFFFF] p-2 rounded-md">
                                          <label className="text-sm font-medium text-gray-700 block mb-1">
                                            Product notes
                                          </label>
                                          <textarea
                                            value={draft.note}
                                            onChange={(e) =>
                                              updateDraft(
                                                product.orderProductId,
                                                {
                                                  note: e.target.value,
                                                },
                                                product,
                                              )
                                            }
                                            className="w-full border rounded p-2 min-h-[90px] resize-none focus:outline-primary"
                                            placeholder="Enter product notes..."
                                          />
                                        </div>
                                      )}

                                      {isCompleted && !isEditingCompleted && (
                                        <>
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div className="bg-[#e1fce9] border border-[#2D5B4A33] rounded-md p-3">
                                              <p className="text-xs font-semibold uppercase tracking-wide text-[#2D5B4A]">
                                                Final Quantity
                                              </p>
                                              <p className="text-sm font-semibold text-[#2D5B4A] mt-1">
                                                {product.productFinalQuantity
                                                  ? `${product.productFinalQuantity} ${product.productType ===
                                                    ProductType.WEIGHT
                                                    ? WeightType.LBS
                                                    : 'Qty'
                                                  }`
                                                  : `${product.productQuantity} ${product.productType ===
                                                    ProductType.WEIGHT
                                                    ? WeightType.LBS
                                                    : 'Qty'
                                                  }`}
                                              </p>
                                            </div>
                                            <div className="border border-[#2D5B4A33] rounded-md p-3">
                                              <p className="text-xs font-semibold uppercase tracking-wide text-[#2D5B4A]">
                                                Product Notes
                                              </p>
                                              <p className="text-sm text-[#2D5B4A] mt-1 break-words whitespace-pre-wrap leading-6">
                                                {product.orderProductNotes?.[0]?.note ||
                                                  errorMessage.NO_NOTES_FOUND}
                                              </p>
                                            </div>
                                          </div>

                                          {product.packing?.length > 0 && (
                                            <div className="mt-3 border border-[#2D5B4A33] rounded-md p-3">
                                              <p className="text-xs font-semibold uppercase tracking-wide text-[#2D5B4A]">
                                                Final Packaging
                                              </p>
                                              <div className="space-y-2 mt-2">
                                                {product.packing
                                                  .filter((pack) => pack.isFinalPacking === 1)
                                                  .map((pack) => (
                                                    <div
                                                      key={pack.orderPackingId}
                                                      className="flex justify-between gap-3 text-sm text-[#2D5B4A]"
                                                    >
                                                      <span className="w-1/4 text-[#2D5B4A]">
                                                        {pack.packageType}
                                                      </span>
                                                      <span className="w-1/4 text-center">
                                                        <span className="text-[#2D5B4A]">Size: </span>
                                                        {pack.packageSize}{' '}
                                                        {WeightType.LBS}
                                                      </span>
                                                      {product.packing.length > 1 && (
                                                        <span className="w-1/4 text-center">
                                                          <span className="text-[#2D5B4A]">
                                                            Pkg Split:{' '}
                                                          </span>
                                                          {pack.packingPercentage}%
                                                        </span>
                                                      )}
                                                      <span className="w-1/4 text-right">
                                                        <span className="text-[#2D5B4A]">Price: </span>
                                                        ${pack.packingPrice}
                                                      </span>
                                                    </div>
                                                  ))}
                                              </div>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {product.productProcessDetail?.productStartDate && (
                                    <div className="px-3 py-1 flex justify-between">
                                      <span className="text-gray-600 font-semibold">
                                        Start date:{' '}
                                        {new Date(
                                          product.productProcessDetail.productStartDate,
                                        ).toLocaleString('en-US')}
                                      </span>
                                      {product.productProcessDetail?.productEndDate && (
                                        <span className="text-gray-600 font-semibold">
                                          End date:{' '}
                                          {new Date(
                                            product.productProcessDetail.productEndDate,
                                          ).toLocaleString('en-US')}
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  <div className="p-3">
                                    {isInitial && !isDirectComplete ? (
                                      <div className="flex flex-wrap justify-end gap-2 mt-1">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleStartProcessing(
                                              speciesInfo.speciesId,
                                              product,
                                            )
                                          }
                                          className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center"
                                        >
                                          Start Processing
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            ensureDraft(product);
                                            setDirectCompleteProductId(
                                              product.orderProductId,
                                            );
                                          }}
                                          className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-primary text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                        >
                                          Complete Processing
                                        </button>
                                      </div>
                                    ) : isInProgress ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleCompleteProcessing(
                                            speciesInfo.speciesId,
                                            productDetailsBySpecies[speciesInfo.speciesId],
                                            product,
                                          )
                                        }
                                        className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] sm:ml-auto px-4 py-1.5 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center"
                                      >
                                        Complete Processing
                                      </button>
                                    ) : isDirectComplete ? (
                                      <div className="flex flex-wrap justify-end gap-2 mt-3">
                                        <button
                                          onClick={() => handleCancelInlineEdit(product)}
                                          type="button"
                                          className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleCompleteProcessing(
                                              speciesInfo.speciesId,
                                              productDetailsBySpecies[
                                              speciesInfo.speciesId
                                              ],
                                              product,
                                            )
                                          }
                                          className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center"
                                        >
                                          Complete Processing
                                        </button>
                                      </div>
                                    ) : isEditingCompleted ? (
                                      <div className="flex flex-wrap justify-end gap-2 mt-3">
                                        <button
                                          onClick={() => handleCancelInlineEdit(product)}
                                          type="button"
                                          className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleCompleteProcessing(
                                              speciesInfo.speciesId,
                                              productDetailsBySpecies[
                                              speciesInfo.speciesId
                                              ],
                                              product,
                                            )
                                          }
                                          className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center"
                                        >
                                          Save Changes
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="inline-block w-full border bg-[#CCEECC] p-2 text-[#2D5B4A] border-[#2D5B4A] text-center font-semibold rounded-md">
                                        Product Completed
                                        <FontAwesomeIcon
                                          icon={faCircleCheck}
                                          className="ml-2 text-primary"
                                        />
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      ) : (
                        <div className="p-3 text-gray-500">No products available.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}

          {showPackagingBottomSheet && activeProductForPackaging !== null && (
            <PackagingBottomSheet
              packageIndex={
                packageIndexByProduct[activeProductForPackaging] ?? 0
              }
              onClose={() => {
                setShowPackagingBottomSheet(false);
                setActiveProductForPackaging(null);
              }}
              onSelect={(option) =>
                handleSelectPackaging(activeProductForPackaging, option)
              }
            />
          )}

          {activeNotesSpecies && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-sm shadow-lg w-full max-w-md p-4">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-lg font-semibold">
                    {activeNotesSpecies.speciesTag} Notes
                  </h2>
                  <button
                    type="button"
                    onClick={() => setActiveNotesSpeciesId(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="bg-gray-100 p-3 rounded">
                    <p className="font-semibold text-gray-700">Check-in notes</p>
                    <p className="mt-1 whitespace-pre-wrap">
                      {activeNotesSpecies.checkIn?.notes ||
                        errorMessage.NO_NOTES_FOUND}
                    </p>
                  </div>
                  <div className="bg-gray-100 p-3 rounded">
                    <p className="font-semibold text-gray-700">Species notes</p>
                    <p className="mt-1 whitespace-pre-wrap">
                      {activeNotesSpecies.notes || errorMessage.NO_NOTES_FOUND}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};